import { RowProps } from '@creditsights/styleguide-web/components/DataView';
import { DataViewItemConfiguration } from '@creditsights/styleguide-web/components/DataView/DataView.types';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { EM_DASH } from '../../Base.constants';
import { Tag } from '../../models';
import { getIssuerRoute } from '../../utils/navigationUtils';
import { getRouteToCallback, getInitialConditionalAction } from '../../utils/formattersUtils';
import { useGlobalModalContext } from '../GlobalModal';
import { MODAL_TYPES } from '../GlobalModal/GlobalModal';
import styles from './CompanyField.module.scss';
import TruncatedCompanyList from './TruncatedCompanyList/TruncatedCompanyList';

const cx = classNames.bind(styles);

interface CompanyFieldProps {
  companies: Tag[];
  item: Object;
  stopMouseUpPropagation?: boolean;
  onItemClick?: (item, e) => void;
  isBold?: boolean;
  row: RowProps;
  configuration?: DataViewItemConfiguration;
}

const CompanyField = ({
  companies,
  item,
  stopMouseUpPropagation = false,
  onItemClick,
  isBold,
  row,
  configuration,
}: CompanyFieldProps): React.ReactElement => {
  const { showModal } = useGlobalModalContext();
  const { isDisabled, isHovered } = row;
  const isDisabledOnHover = isDisabled && isHovered;
  const linkItems = useMemo(
    () => companies.map((company) => ({ title: company.name ?? '', url: getIssuerRoute(company.tagId) })),
    [companies]
  );

  const onMouseUp = (e) => {
    if (!isEmpty(companies) && stopMouseUpPropagation && !isDisabled) {
      e.stopPropagation();
    }
  };
  const routeToCallback = getRouteToCallback(configuration, item);
  const initialConditionalAction = getInitialConditionalAction(configuration, row, { onItemClick }, item);

  if (isEmpty(companies)) {
    return (
      <Link
        to={routeToCallback}
        className={cx('em-dash', { 'is-bolded': isBold, 'gray-out': isDisabledOnHover })}
        onMouseUp={onMouseUp}
        onClick={initialConditionalAction}
        tabIndex={-1}
        data-testid="no-companies-link"
      >
        {EM_DASH}
      </Link>
    );
  }

  const onLinkClick = () => {
    showModal(MODAL_TYPES.COMPANIES_WEIGHT, { companies });
  };

  return (
    <div className={cx('body')} data-testid="company-field-list">
      <TruncatedCompanyList
        items={linkItems}
        onClick={onLinkClick}
        onMouseUp={onMouseUp}
        isBold={isBold}
        isDisabled={isDisabled}
      />
    </div>
  );
};

export default CompanyField;
